import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { CookieConsentProvider } from "@/providers/CookieConsentProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { DataModeProvider } from "@/contexts/DataModeContext";
import { ETDProvider } from "@/contexts/ETDContext";
import { ErrorProvider } from "@/contexts/ErrorContext";
import { ErrorBoundary } from "@/components/error";
import { UserPreferencesProvider } from "@/providers/UserPreferencesProvider";
import { WootricProvider } from "@/providers/WootricProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import { AppWithProviders } from "@/App";
import etdProductData from "@/data/etd-product-data.json";
import { WorkspaceSettingsProvider, type WorkspaceSettings } from "@/contexts/WorkspaceSettingsContext";

interface AppContainerProps {
  settings?: { workspaceUrl?: string; defaultUrl?: string } & Record<string, unknown>;
}

function AppContent() {
  const { user: wootricUser } = useUserProfile();

  return (
    <WootricProvider user={wootricUser}>
      <Toaster richColors duration={5000} closeButton />
      <AppWithProviders />
    </WootricProvider>
  );
}

export default function AppContainer(props: AppContainerProps) {
  const workspaceSettings: WorkspaceSettings = {
    workspaceUrl: props.settings?.workspaceUrl,
    defaultUrl: props.settings?.defaultUrl,
  };

  return (
    <ErrorBoundary>
      <QueryProvider>
        <CookieConsentProvider>
          <ErrorProvider>
            <ETDProvider fallbackProductData={etdProductData}>
              <DataModeProvider>
                <WorkspaceSettingsProvider settings={workspaceSettings}>
                  <UserPreferencesProvider>
                    <TooltipProvider>
                      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
                        <AppContent />
                      </ThemeProvider>
                    </TooltipProvider>
                  </UserPreferencesProvider>
                </WorkspaceSettingsProvider>
              </DataModeProvider>
            </ETDProvider>
          </ErrorProvider>
        </CookieConsentProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}
